import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { getCheckoutSettings } from '../../../../api/Providers/CheckoutSettings';
import { hasNSLCPolicy } from '../../../../modules/ProviderState';
import Currency from '../../../../modules/Currency';
import {
  isMonetizedNCD,
  getCancellationCharge,
  isGoogleBooked,
  hasLateCancellationPolicy,
  isPrepayAppointment,
  isPrepayNCDAppointment,
  isWithinLateCancelTime,
  isRecurring,
  isWithinNCDCancellationWindow,
  canCancelNCDAppointment,
} from '../../../../modules/ProAppointmentState';

import { ApptCancelModal } from './ApptCancelModal';
import { ActionProps } from './ApptCancelModalContent';
import CancelAppointmentModal, { CancelAppointmentModalProps } from './CancelAppointmentModal';
import { ProAppointmentDetails, getAppointmentCancellationLink } from '../../../../api/Providers/Appointments';
import { useAsyncEffect, useIsEnabled } from '../../../../hooks';
import analytics from '../../../../modules/analytics';
import { IRootDispatch } from '../../../../store/models';
import { openExternalUrl } from '../../../../modules/openExternalUrl';
import { generateSmsUrl } from '../../../consumer/sharing/social/sms';
import { IProviderClient } from '../../../../api/Providers/Clients/types';
import { PrivilegedProvider } from '../../../../api/Providers';

type ApptCancelModalContainerProps = {
  visible: boolean;
  provider: PrivilegedProvider,
  client: IProviderClient | null;
  appointment: ProAppointmentDetails;
  markAsLateCancelConfirm: (options: { charge: string | null, delete: boolean }) => void;
  deleteConfirm: () => void;
  deleteAllConfirm: () => void;
  closeApptCancelModal: (confirmedCancel: boolean) => void;
  isUsingCardOnFile: boolean;
};

export const NCD_CANCELLATION_RESTRICTION: string = 'ncd_cancellation_restriction_fly3373_20220712';

const ApptCancelModalContainer: React.FC<ApptCancelModalContainerProps> = ({
  visible,
  provider,
  client,
  appointment,
  markAsLateCancelConfirm,
  deleteConfirm,
  deleteAllConfirm,
  closeApptCancelModal,
  isUsingCardOnFile,
}) => {
  const { snackbar } = useDispatch<IRootDispatch>();
  const [modalProps, setModalProps] = useState<any>({
    id: 'none', title: '', actions: [],
  });
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const switchEnabled = useIsEnabled(NCD_CANCELLATION_RESTRICTION);
  const [
    cancelAppointmentModalProps,
    setCancelAppointmentModalProps,
  ] = useState<CancelAppointmentModalProps | null>(null);

  const setNCDRestrictionProps = async (): Promise<void> => {
    const { management_link: link } = await getAppointmentCancellationLink(
      provider.id,
      appointment.id,
    );
    const message = `Action Required. ${provider.name} has requested that you cancel your appointment: ${link}`;
    const phone = client?.sms_status;
    const recipient = phone?.mobile_phone_number?.national;
    const smsURL = generateSmsUrl(recipient, message);
    const onSmsClick = () => openExternalUrl(smsURL, null);
    setCancelAppointmentModalProps({
      visible,
      appointment,
      onSmsClick,
      inCancellationWindow: isWithinNCDCancellationWindow(appointment),
      in24HourCancellationWindow: isWithinLateCancelTime(appointment),
      proCanCancel: canCancelNCDAppointment(appointment),
      onCancelAppointmentClick: deleteConfirm,
      closeCancelAppointmentModal: closeApptCancelModal,
      proLateCancellationFeePercent: provider.late_cancellation_fee_percent,
    });
  };

  const resolveSettings = (response: any): any => {
    const checkoutSettings = {
      service_tax_rate: parseFloat(response.service_tax_rate || 0),
      product_tax_rate: parseFloat(response.product_tax_rate || 0),
    };
    return checkoutSettings;
  };

  const setNSLCModalProps = async (): Promise<any> => {
    const checkoutSettings = resolveSettings(await getCheckoutSettings(provider.id));
    const taxRate = checkoutSettings.service_tax_rate / 100;
    const charge = getCancellationCharge(appointment) * (1 + Number(taxRate));
    const showCardAuthCopy = appointment.auth_amount;
    const body = (
      !showCardAuthCopy
        ? `This will charge your client ${Currency.shortFormat(charge)} based on the price of the original service booked.`
        : `We've already secured a payment for this appointment. Would you like to keep a fee of ${Currency.shortFormat(charge)} based on the price of the original service booked?`
    );
    const props = {
      id: 'cancel nslc modal',
      title: 'Cancel this appointment?',
      body,
      actions: [
        {
          id: 'cancel charge',
          text: !showCardAuthCopy ? 'Charge' : 'Keep Cancel Fee',
          onClick: () => {
            markAsLateCancelConfirm({
              charge: appointment.cancellation_charge,
              delete: true,
            });
            closeApptCancelModal(true);
          },
          appearance: 'primary',
        },
        {
          id: 'cancel no charge',
          text: !showCardAuthCopy ? 'Don\'t Charge' : 'Return Full Amount',
          onClick: () => {
            setModalVisible(false);
            deleteConfirm();
            closeApptCancelModal(true);
          },
          appearance: 'primary',
        },
        {
          id: 'cancel nevermind',
          text: 'Never Mind',
          onClick: () => closeApptCancelModal(false),
          appearance: 'outline',
        },
      ],
    };
    setModalProps(props);
  };

  const setRecurringAppointmentsModalProps = (): void => {
    const props = {
      id: 'cancel recurring appointments modal',
      title: 'Cancel this appointment only, or all future appointments in series?',
      actions: [
        {
          id: 'cancel this appointment only',
          text: 'This Appointment Only',
          onClick: () => {
            deleteConfirm();
            closeApptCancelModal(true);
          },
          appearance: 'primary',
        },
        {
          id: 'cancel all appointments in series',
          text: 'All Appointments in Series',
          onClick: () => {
            deleteAllConfirm();
            closeApptCancelModal(true);
          },
          appearance: 'primary',
        },
        {
          id: 'cancel nevermind',
          text: 'Never Mind',
          onClick: () => closeApptCancelModal(false),
          appearance: 'outline',
        },
      ],
    };
    setModalProps(props);
  };

  const setDefaultModalProps = (): void => {
    const title = isGoogleBooked(appointment)
      ? 'Remove from StyleSeat calendar?'
      : 'Cancel appointment?';

    const DEFAULT_BODY = 'Are you sure you want to cancel this appointment?';
    const DISABLED_NSLC_CHARGE_BODY = 'You cannot charge a fee for this appt. It was booked before you selected a no-show/late-cancellation policy.';
    const PREPAID_LATE_CANCEL_CHARGE_BODY = 'Your client prepaid for this appointment, They will not be charged a late cancellation fee.';
    const NCD_REFUND_CANCEL_BODY = 'The client will be refunded the deposit and they will not be charged a fee.';

    const hasCancellationCharges = hasLateCancellationPolicy(appointment);
    const isPrepaidAppointment = isPrepayAppointment(appointment);
    const isPrepaidNCDAppointment = isPrepayNCDAppointment(appointment);
    const withinLateCancelLimit = isWithinLateCancelTime(appointment);
    const hasProActiveNslcPolicy = hasNSLCPolicy(provider);

    let body = DEFAULT_BODY;
    // booked before pro added nslc
    if (
      hasProActiveNslcPolicy
      && withinLateCancelLimit
      && isUsingCardOnFile
      && provider.cancellation_policy > 1
      && !hasCancellationCharges
      && !isPrepaidAppointment
    ) {
      body = DISABLED_NSLC_CHARGE_BODY;
    }

    if (isPrepaidAppointment && hasProActiveNslcPolicy) {
      body = PREPAID_LATE_CANCEL_CHARGE_BODY;
    }

    if (isPrepaidNCDAppointment) {
      body = NCD_REFUND_CANCEL_BODY;
    }

    const props = {
      id: 'cancel default modal',
      title,
      body,
      actions: [
        {
          id: 'cancel confirm',
          text: 'Confirm',
          appearance: 'primary',
          onClick: () => {
            deleteConfirm();
            closeApptCancelModal(true);
          },
        },
        {
          id: 'cancel nevermind',
          text: 'Never Mind',
          onClick: () => closeApptCancelModal(false),
          appearance: 'outline',
        },
      ],
    };
    setModalProps(props);
  };

  // Set modal visibility only after modal props are set by one of the functions
  useEffect(() => {
    if (modalProps.id !== 'none') {
      setModalVisible(visible);
    }
  }, [modalProps]);

  useEffect(() => {
    if (modalVisible) {
      analytics.track(
        'pro_appointment_cancel_appointment_viewed',
        {
          type: 'open cancel appointment modal',
          appointment_id: appointment.id,
          client_id: client?.id,
          provider_id: provider.id,
          modal: modalProps.id,
        },
      );
    }
  }, [
    modalVisible,
    modalProps?.id,
    appointment?.id,
    client?.id,
    provider?.id,
  ]);

  useAsyncEffect(async () => {
    if (!appointment || !provider) return;
    if (switchEnabled && isMonetizedNCD(appointment)) {
      await setNCDRestrictionProps();
    } else if (
      // provider attempts to cancel appt within 24 hrs after appt start time and there is
      // a cancellation fee greater than $0 dollars, give provider option to charge late
      // cancel fee
      hasLateCancellationPolicy(appointment)
      && isWithinLateCancelTime(appointment)
    ) {
      await setNSLCModalProps();
    } else if (isRecurring(appointment)) {
      setRecurringAppointmentsModalProps();
    } else {
      setDefaultModalProps();
    }
  }, [
    switchEnabled,
    visible,
    appointment,
    provider,
    client,
  ]);

  const handleActionClick = useCallback((
    button: ActionProps,
    event: React.SyntheticEvent<EventTarget>,
  ) => {
    analytics.track(
      'pro_appointment_cancel_appointment_click',
      {
        type: button.id,
        appointment_id: appointment.id,
        client_id: client?.id,
        provider_id: provider.id,
        modal: modalProps.id,
      },
    );
    if (button.snackbar) {
      snackbar.open(button.snackbar);
      setTimeout(() => snackbar.close(), 5000);
    }
    button.onClick(event);
  }, [
    appointment?.id,
    client?.id,
    modalProps?.id,
    provider?.id,
    snackbar,
  ]);
  return (
    <React.Fragment>
      {modalVisible && (
        <ApptCancelModal
          providerId={provider.id}
          clientId={client?.id}
          appointmentId={appointment.id}
          onActionClick={handleActionClick}
          {...modalProps}
        />
      )}
      {visible && cancelAppointmentModalProps && (
        <CancelAppointmentModal {...cancelAppointmentModalProps} />
      )}
    </React.Fragment>
  );
};

export default ApptCancelModalContainer;
